import { StandardEvents } from '@shopify/events';

/**
 * Keeps the subscribe price/per-can captions in sync with the selected
 * delivery frequency and with pack-size (variant) changes, and keeps the
 * main price display in sync too if one exists on the page.
 *
 * Neither direction happens for free: snippets/price.liquid has no concept
 * of selling plans, so choosing "Subscribe" (or a different frequency) has
 * to swap the displayed price in by hand; and this picker lives outside
 * <variant-picker>'s own fieldsets, so a pack-size change never touches it
 * on its own.
 *
 * Reuses the theme's existing section re-fetch (dispatched by <variant-picker>
 * on pack-size change) rather than hand-formatting money in JS: the fetched
 * HTML already has the new variant's prices rendered correctly by Liquid
 * (currency, locale, sale pricing all handled), so this only ever copies
 * text out of it. Frequency prices work the same way — each frequency radio
 * carries its own pre-formatted `data-price`/`data-cadence`/`data-per-can`
 * from snippets/csc-selling-plan-picker.liquid, so switching frequency is
 * also just a text copy, never a reformat.
 */
class CscSellingPlanPicker extends HTMLElement {
  connectedCallback() {
    const fieldset = this.querySelector('.csc-subscribe');
    if (!fieldset) return;

    const priceContainer = this.#findPriceContainer();
    // Baseline one-time price as rendered at page load, restored whenever
    // "One-time purchase" is selected.
    this.oneTimePriceHTML = priceContainer ? priceContainer.innerHTML : null;

    this.#bindRadios();
    this.#bindFrequency();

    this.section = this.closest('.shopify-section');
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #findPriceContainer() {
    return this.closest('.shopify-section')?.querySelector('product-price [ref="priceContainer"]') ?? null;
  }

  #bindRadios() {
    this.querySelectorAll('input[name="selling_plan"]').forEach((radio) => {
      radio.addEventListener('change', this.#syncPrice);
    });
  }

  #bindFrequency() {
    this.querySelectorAll('.csc-subscribe__frequency input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', this.#handleFrequencyChange);
    });
  }

  #handleFrequencyChange = (event) => {
    const freqRadio = event.currentTarget;
    const subscribeRadio = this.querySelector('input[name="selling_plan"][data-role="subscribe"]');
    if (!subscribeRadio) return;

    subscribeRadio.value = freqRadio.value;
    subscribeRadio.checked = true;

    const option = subscribeRadio.closest('.csc-subscribe__option');
    const priceEl = option?.querySelector('.csc-subscribe__price');
    const cadenceEl = priceEl?.querySelector('.csc-subscribe__cadence');
    const perCanEl = option?.querySelector('.csc-subscribe__per-can');

    if (priceEl?.firstChild) priceEl.firstChild.textContent = `${freqRadio.dataset.price} `;
    if (cadenceEl) cadenceEl.textContent = `/ ${freqRadio.dataset.cadence}`;
    if (perCanEl && freqRadio.dataset.perCan) perCanEl.textContent = freqRadio.dataset.perCan;

    this.#syncPrice();
  };

  #handleProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;

        const newFieldset = html.querySelector('csc-selling-plan-picker .csc-subscribe');
        const currentFieldset = this.querySelector('.csc-subscribe');
        if (newFieldset && currentFieldset) {
          const checkedValue = currentFieldset.querySelector('input[name="selling_plan"]:checked')?.value ?? '';
          const checkedFrequency = currentFieldset.querySelector('.csc-subscribe__frequency input:checked')?.value;

          currentFieldset.replaceWith(newFieldset);

          newFieldset.querySelectorAll('input[name="selling_plan"]').forEach((radio) => {
            radio.checked = radio.value === checkedValue;
          });
          if (checkedFrequency) {
            newFieldset.querySelectorAll('.csc-subscribe__frequency input').forEach((radio) => {
              radio.checked = radio.value === checkedFrequency;
            });
          }

          this.#bindRadios();
          this.#bindFrequency();
        }

        // New pack size means a new one-time price too — refresh the
        // baseline from the freshly fetched page, not the live DOM (which
        // <product-price>'s own listener may or may not have updated yet).
        const newPriceContainer = html.querySelector('product-price [ref="priceContainer"]');
        if (newPriceContainer) {
          this.oneTimePriceHTML = newPriceContainer.innerHTML;
        }

        this.#syncPrice();
      })
      .catch(() => {
        // Section re-fetch failed or was aborted (e.g. rapid re-selection) —
        // leave the price as-is rather than showing a stale or broken value.
      });
  };

  #syncPrice = () => {
    const priceContainer = this.#findPriceContainer();
    if (!priceContainer) return;

    const checked = this.querySelector('input[name="selling_plan"]:checked');
    const isSubscription = checked && checked.value !== '';

    if (!isSubscription) {
      if (this.oneTimePriceHTML !== null) priceContainer.innerHTML = this.oneTimePriceHTML;
      return;
    }

    const priceEl = checked.closest('.csc-subscribe__option')?.querySelector('.csc-subscribe__price');
    if (priceEl) priceContainer.innerHTML = priceEl.innerHTML;
  };
}

if (!customElements.get('csc-selling-plan-picker')) {
  customElements.define('csc-selling-plan-picker', CscSellingPlanPicker);
}
